import { useTranslation } from 'react-i18next';
import React, { ComponentType, MouseEvent } from 'react';
import {
  OutgoingMessageChatMessageKind,
  OutgoingMessageResponseStatus,
} from '@engyalo/salesdesk-chat-socket-ts-client';
import { useFeatureFlag, flag } from '../../../hooks/useFeatureFlag';
import { formatMessageTime } from '../../../helpers/formatterDates';
import { environmentUtils } from '../../../utils';
import { ErrorMessage, MessageRow, MessageWrapper } from './styled';
import TextMessage from './TextMessage';
import AudioMessage from './AudioMessage';
import ImageMessage from './ImageMessage';
import VideoMessage from './VideoMessage';
import FileMessage from './FileMessage';
import InteractiveMessage from './InteractiveMessage';
import { ChatMessageType, SpecificMessageProps } from '../Message';
import LocationMessage from './LocationMessage';
import { getMessageStatus } from './utils';
import ContactInfoArea from './ContactInfoArea';

export type ChatMessageComponentProps = Omit<ChatMessageType, 'kind'>;

export const MESSAGE_COMPONENTS: Record<OutgoingMessageChatMessageKind, ComponentType<ChatMessageComponentProps>> = {
  text: TextMessage,
  audio: AudioMessage,
  image: ImageMessage,
  video: VideoMessage,
  document: FileMessage,
  location: LocationMessage,
  interactive: InteractiveMessage,
};

const ChatMessage: React.FC<SpecificMessageProps> = ({ chat, onClick }) => {
  const { t, i18n } = useTranslation();
  const isDeliveredStatusEnabled = useFeatureFlag(flag.DELIVERED_STATUS);
  const isReadStatusEnabled = useFeatureFlag(flag.READ_STATUS);

  if (!chat) {
    return null;
  }

  const { kind, ...rest } = chat;
  const { pending, error, delivered, read, authorRole, name: nameProp, username, timestamp, status } = chat;
  const name = nameProp || username;

  const hasDeliveredStatus = status === OutgoingMessageResponseStatus.ResponseStatusDelivered;
  const hasReadStatus = status === OutgoingMessageResponseStatus.ResponseStatusRead;
  const messageStatus = getMessageStatus({
    pending,
    delivered: isDeliveredStatusEnabled && (delivered || hasDeliveredStatus),
    read: isReadStatusEnabled && (read || hasReadStatus),
  });

  const isAgent = authorRole === 'agent';
  const isBot = authorRole === 'bot';
  const textMessageProps = {
    displayName: isBot ? `${t('flow')}: ${environmentUtils.getEnvironmentInstance()}` : name,
    sentAt: formatMessageTime(Number(timestamp), i18n.language),
    messageStatus: isAgent ? messageStatus : undefined,
    variant: error ? 'error' : undefined,
  };

  const MessageComponent = MESSAGE_COMPONENTS[kind];

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (error) {
      event.preventDefault();
    }

    onClick?.();
  };

  return (
    <MessageRow error={error} onClick={handleClick}>
      <MessageWrapper authorRole={authorRole}>
        <ContactInfoArea name={name} authorRole={authorRole} />
        <MessageComponent {...rest} {...(kind === OutgoingMessageChatMessageKind.Text && textMessageProps)} />
      </MessageWrapper>
      {error && <ErrorMessage>{t('messages.error')}</ErrorMessage>}
    </MessageRow>
  );
};

export default ChatMessage;
